import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, Mail, Phone, Globe2 } from "lucide-react";
import { SITE } from "@/lib/site";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    country: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => {
    let value = e.target.value;
    
    // For phone field, only allow numbers
    if (k === "phone") {
      value = value.replace(/\D/g, "");
    }
    
    setForm((f) => ({ ...f, [k]: value }));
    
    // Real-time validation for email local part
    if (k === "email") {
      const emailParts = value.split("@");
      const localPart = emailParts[0] || "";
      if (localPart.length > 30 && value.includes("@") && touched.email) {
        setErrors((prev) => ({ ...prev, email: "Email local part (before @) cannot exceed 30 characters" }));
      } else if (errors.email && localPart.length <= 30) {
        setErrors((prev) => ({ ...prev, email: "" }));
      }
    }
    
    // Clear error for field when user starts typing (only if touched)
    if (touched[k] && errors[k] && k !== "email") {
      setErrors((prev) => ({ ...prev, [k]: "" }));
    }
  };

  const onFocus = (k) => () => {
    setTouched((prev) => ({ ...prev, [k]: true }));
  };

  const onBlur = (k) => () => {
    setTouched((prev) => ({ ...prev, [k]: true }));
    validateField(k, form[k]);
  };

  const validateField = (fieldName, value) => {
    let error = "";

    switch (fieldName) {
      case "name":
        const nameRegex = /^[A-Za-z\s]{1,30}$/;
        if (!value) {
          error = "Name is required";
        } else if (!nameRegex.test(value)) {
          error = "Name must contain only letters and spaces (max 30 characters)";
        }
        break;

      case "email":
        if (value) { // Only validate if email is provided
          const emailParts = value.split("@");
          const localPart = emailParts[0] || "";
          const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
          
          if (localPart.length > 30) {
            error = "Email local part (before @) cannot exceed 30 characters";
          } else if (!emailRegex.test(value)) {
            error = "Please enter a valid email address (e.g., name@domain.com)";
          }
        }
        break;

      case "phone":
        if (!value) {
          error = "Phone number is required";
        } else if (value.length < 5) {
          error = "Phone number must have at least 5 digits";
        } else if (value.length > 15) {
          error = "Phone number cannot exceed 15 digits";
        }
        break;

      case "company":
        if (value && value.length > 50) {
          error = "Company name cannot exceed 50 characters";
        }
        break;

      case "country":
        const countryRegex = /^[A-Za-z\s\-']{0,80}$/;
        if (value && !countryRegex.test(value)) {
          error = "Country name should contain only letters, spaces, hyphens, or apostrophes (max 80 characters)";
        }
        break;
    }

    setErrors((prev) => ({ ...prev, [fieldName]: error }));
    return !error;
  };

  // Validate all required fields on form submit
  const validateForm = () => {
    const requiredFields = ["name", "phone"];
    let isValid = true;
    
    // Check required fields first
    for (const field of requiredFields) {
      if (!form[field]) {
        setErrors((prev) => ({ 
          ...prev, 
          [field]: field === "name" ? "Name is required" : "Phone number is required" 
        }));
        setTouched((prev) => ({ ...prev, [field]: true }));
        isValid = false;
      }
    }
    
    // Validate other fields if they have values
    const optionalFields = ["email", "company", "country"];
    optionalFields.forEach((field) => {
      if (form[field]) {
        const fieldIsValid = validateField(field, form[field]);
        if (!fieldIsValid) isValid = false;
      }
    });
    
    // If name exists but invalid format
    if (form.name && form.name.length > 0) {
      const nameRegex = /^[A-Za-z\s]{1,30}$/;
      if (!nameRegex.test(form.name)) {
        setErrors((prev) => ({ 
          ...prev, 
          name: "Name must contain only letters and spaces (max 30 characters)" 
        }));
        isValid = false;
      }
    }
    
    // If phone exists but invalid format
    if (form.phone && form.phone.length > 0) {
      if (form.phone.length < 5) {
        setErrors((prev) => ({ 
          ...prev, 
          phone: "Phone number must have at least 5 digits" 
        }));
        isValid = false;
      } else if (form.phone.length > 15) {
        setErrors((prev) => ({ 
          ...prev, 
          phone: "Phone number cannot exceed 15 digits" 
        }));
        isValid = false;
      }
    }
    
    return isValid;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    
    // Mark all required fields as touched
    setTouched({
      name: true,
      phone: true,
      email: true,
      company: true,
      country: true,
    });

    // Run validation before submission
    if (!validateForm()) {
      toast.error("Please fill all required fields correctly");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        "/send-mail.php",
        form,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        toast.success("Thanks! We'll reach out within one working day.");

        setForm({
          name: "",
          email: "",
          phone: "",
          company: "",
          country: "",
        });
        setErrors({});
        setTouched({});
        setSubmitted(false);
      } else {
        toast.error(
          response.data.message || "Submission failed"
        );
      }

    } catch (err) {
      console.error(err);
      toast.error(
        "Something broke on our end. Please try again or WhatsApp us."
      );
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine if error should be shown
  const shouldShowError = (fieldName) => {
    return (touched[fieldName] || submitted) && errors[fieldName];
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 bg-zinc-950"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/30 to-transparent" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="lg:col-span-5"
        >
          <p className="text-yellow-400 font-mono-ui text-xs tracking-[0.22em] uppercase mb-4">
            · Let's talk
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05]">
            Ready to add some
            <br />
            <span className="brand-gradient-text">zest</span> to your growth?
          </h2>
          <p className="mt-6 text-zinc-400 text-base sm:text-lg leading-relaxed max-w-md">
            Drop your details. We'll come back within one working day with a
            short read on where we can move the needle for you.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={`mailto:${SITE.email}`}
              data-testid="contact-email-link"
              className="flex items-center gap-4 group"
            >
              <span className="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-yellow-400/10 border border-yellow-400/25 text-yellow-300">
                <Mail size={18} />
              </span>
              <span className="text-zinc-200 group-hover:text-yellow-300 transition-colors">
                {SITE.email}
              </span>
            </a>
            {SITE.phones.map((p) => (
              <a
                key={p}
                href={`tel:${p.replace(/\s/g, "")}`}
                data-testid={`contact-phone-${p.replace(/\s|\+/g, "")}`}
                className="flex items-center gap-4 group"
              >
                <span className="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-yellow-400/10 border border-yellow-400/25 text-yellow-300">
                  <Phone size={18} />
                </span>
                <span className="text-zinc-200 group-hover:text-yellow-300 transition-colors">
                  {p}
                </span>
              </a>
            ))}
            <div className="flex items-center gap-4">
              <span className="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-yellow-400/10 border border-yellow-400/25 text-yellow-300">
                <Globe2 size={18} />
              </span>
              <span className="text-zinc-200">{SITE.website}</span>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="lg:col-span-7 rounded-3xl border border-white/10 bg-zinc-900/40 p-7 sm:p-10"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field 
              label="Your name" 
              testid="form-name" 
              error={shouldShowError("name") ? errors.name : ""}
              required
            >
              <input
                required
                value={form.name}
                onChange={onChange("name")}
                onFocus={onFocus("name")}
                onBlur={onBlur("name")}
                placeholder="Aarav Mehta"
                className={`form-input ${shouldShowError("name") ? "border-red-500/50 focus:border-red-500" : ""}`}
                data-testid="input-name"
                maxLength={30}
              />
            </Field>
            
            <Field 
              label="Email" 
              testid="form-email" 
              error={shouldShowError("email") ? errors.email : ""}
            >
              <input
                type="email"
                value={form.email}
                onChange={onChange("email")}
                onFocus={onFocus("email")}
                onBlur={onBlur("email")}
                placeholder="you@brand.com (optional)"
                className={`form-input ${shouldShowError("email") ? "border-red-500/50 focus:border-red-500" : ""}`}
                data-testid="input-email"
                maxLength={100}
              />
              <span className="block text-xs text-zinc-500 mt-1">
                Optional · Max 30 characters before @
              </span>
            </Field>
            
            <Field 
              label="Phone / WhatsApp" 
              testid="form-phone" 
              error={shouldShowError("phone") ? errors.phone : ""}
              required
            >
              <input
                type="tel"
                required
                value={form.phone}
                onChange={onChange("phone")}
                onFocus={onFocus("phone")}
                onBlur={onBlur("phone")}
                placeholder="9876543210"
                className={`form-input ${shouldShowError("phone") ? "border-red-500/50 focus:border-red-500" : ""}`}
                data-testid="input-phone"
                maxLength={15}
                inputMode="numeric"
                pattern="[0-9]*"
              />
              <span className="block text-xs text-zinc-500 mt-1">
                Required · Only numbers · 5-15 digits
              </span>
            </Field>
            
            <Field 
              label="Company" 
              testid="form-company" 
              error={shouldShowError("company") ? errors.company : ""}
            >
              <input
                value={form.company}
                onChange={onChange("company")}
                onFocus={onFocus("company")}
                onBlur={onBlur("company")}
                placeholder="Brand / Company (optional)"
                className={`form-input ${shouldShowError("company") ? "border-red-500/50 focus:border-red-500" : ""}`}
                data-testid="input-company"
                maxLength={50}
              />
              <span className="block text-xs text-zinc-500 mt-1">
                Optional · Max 50 characters
              </span>
            </Field>

            <Field 
              label="Country" 
              testid="form-country" 
              error={shouldShowError("country") ? errors.country : ""}
            >
              <input
                value={form.country}
                onChange={onChange("country")}
                onFocus={onFocus("country")}
                onBlur={onBlur("country")}
                placeholder="India / United States ... (optional)"
                className={`form-input sm:col-span-2 ${shouldShowError("country") ? "border-red-500/50 focus:border-red-500" : ""}`}
                data-testid="input-country"
                maxLength={80}
              />
              <span className="block text-xs text-zinc-500 mt-1">
                Optional · Max 80 characters
              </span>
            </Field>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-button"
            className="mt-7 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-yellow-400 text-zinc-950 font-bold hover:bg-yellow-300 transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Sending…
              </>
            ) : (
              <>
                Send it over <Send size={16} />
              </>
            )}
          </button>
          <p className="mt-4 text-xs text-zinc-500 font-mono-ui">
            We reply within 1 working day · No spam, ever.
          </p>

          <style>{`
            .form-input {
              width: 100%;
              padding: 0.85rem 1rem;
              border-radius: 0.75rem;
              background: rgba(255,255,255,0.03);
              border: 1px solid rgba(255,255,255,0.08);
              color: #fafafa;
              font-size: 0.95rem;
              transition: all 0.2s;
              outline: none;
            }
            .form-input::placeholder { color: #71717a; }
            .form-input:focus {
              border-color: rgba(250, 204, 21, 0.55);
              box-shadow: 0 0 0 4px rgba(250, 204, 21, 0.08);
              background: rgba(255,255,255,0.04);
            }
            .form-input.border-red-500\\/50 {
              border-color: rgba(239, 68, 68, 0.5);
            }
            .form-input.border-red-500\\/50:focus {
              border-color: rgb(239, 68, 68);
              box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
            }
            /* Remove number input spinners */
            input[type="tel"]::-webkit-inner-spin-button,
            input[type="tel"]::-webkit-outer-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }
            input[type="tel"] {
              -moz-appearance: textfield;
            }
          `}</style>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, children, testid, error, required }) {
  return (
    <div className="block" data-testid={testid}>
      <label>
        <span className="block text-xs font-mono-ui uppercase tracking-[0.18em] text-zinc-400 mb-2">
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </span>
        {children}
      </label>
      {error && (
        <span className="block text-xs text-red-400 mt-1 font-medium">
          {error}
        </span>
      )}
    </div>
  );
}